"use client";

import { useEffect, useRef } from "react";
import { errorText, useT } from "@/lib/i18n";
import type { UnitStatus } from "@/lib/types";

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "danger" | "ghost" | "default";
  size?: "sm" | "xl" | "md";
  block?: boolean;
  busy?: boolean;
};

export function Button({ variant = "default", size = "md", block, busy, className = "", children, disabled, ...rest }: BtnProps) {
  const cls = ["btn", variant !== "default" && `btn-${variant}`, size !== "md" && `btn-${size}`, block && "btn-block", className]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" {...rest} className={cls} disabled={disabled || busy} aria-busy={busy || undefined}>
      {children}
    </button>
  );
}

export function Card({ title, actions, children, className = "" }: { title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-head">
          {title && <h2 style={{ margin: 0 }}>{title}</h2>}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Banner({ kind = "info", children }: { kind?: "info" | "warn" | "danger" | "ok"; children: React.ReactNode }) {
  return (
    <div className={`banner banner-${kind}`} role={kind === "danger" ? "alert" : undefined}>
      {children}
    </div>
  );
}

export function Field({ label, hint, error, children, className = "" }: { label: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode; children: React.ReactNode; className?: string }) {
  // The control sits inside the <label>, which associates them without wiring ids by hand.
  return (
    <div className={`field ${className}`}>
      <label>
        <span className="label">{label}</span>
        {children}
      </label>
      {hint && <span className="hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

export function Spinner() {
  const t = useT();
  return (
    <p className="muted" role="status">
      {t("common.loading")}
    </p>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const t = useT();
  return <Banner kind="danger">{errorText(t, error)}</Banner>;
}

export function StatusBadge({ status }: { status: UnitStatus }) {
  const t = useT();
  return <span className={`badge badge-${status}`}>{t(`status.${status}`)}</span>;
}

export function SampleBadge() {
  const t = useT();
  return (
    <span className="badge badge-sample" title={t("common.sample_hint")}>
      {t("common.sample")}
    </span>
  );
}

export function YesNo({ value }: { value: boolean }) {
  const t = useT();
  return <span className={`badge ${value ? "badge-ok" : "badge-no"}`}>{value ? t("common.yes") : t("common.no")}</span>;
}

/** A Netro ID, set in a monospace face so 0/O and 1/I stay distinguishable. */
export function IdText({ value, hero }: { value: string; hero?: boolean }) {
  return <span className={`id-text${hero ? " id-hero" : ""}`}>{value}</span>;
}

export function Dialog({ open, title, onClose, children, wide }: { open: boolean; title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const t = useT();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // showModal() focuses the first focusable element, which is the ✕ button, and that
      // overrides React's autoFocus. Put the cursor in the first field instead.
      d.querySelector<HTMLElement>("input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled)")?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} onCancel={onClose} aria-label={title} style={wide ? { width: "min(980px, calc(100vw - 1.5rem))" } : undefined}>
      {open && (
        <div className="dialog-body">
          <div className="row-between">
            <h2 style={{ margin: 0 }}>{title}</h2>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label={t("common.close")}>
              ✕
            </Button>
          </div>
          <div className="stack" style={{ marginTop: "0.75rem" }}>
            {children}
          </div>
        </div>
      )}
    </dialog>
  );
}

export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: { key: K; label: string }[]; value: K; onChange: (k: K) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button key={tab.key} role="tab" type="button" className="tab" aria-selected={tab.key === value} onClick={() => onChange(tab.key)}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Pager({ total, offset, limit, onChange }: { total: number; offset: number; limit: number; onChange: (offset: number) => void }) {
  const t = useT();
  if (total <= limit) return null;
  const from = offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="pager">
      <span className="muted small">{t("common.range", { from, to, total })}</span>
      <Button size="sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        ‹ {t("common.prev")}
      </Button>
      <Button size="sm" disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>
        {t("common.next")} ›
      </Button>
    </div>
  );
}
